import Button from 'components/Button';
import FormResult from 'components/Form/FormResult';
import Icon from 'components/Icon';
import Link from 'components/Link';
import NavigationTabs from 'components/Navigation/NavigationTabs';
import SectionHeader from 'components/SectionHeader';
import TablePagination from 'components/Table/TableComponents/TablePagination';
import TableStatic from 'components/Table/TableStatic';
import TooltipWrapper from 'components/Tooltip';
import usePaginatedData from 'hooks/usePaginatedData';
import {useMemo} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useBackend} from 'state/backend';
import {jt, t} from 'ttag';
import {setUrl} from 'utils/request';
import {onApiError, onApiSuccess} from 'utils/request-axios/utils';

import useToggleTeamMemberGroup from './services';
import {Group, TeamMemberGroupsBackendData} from './types';

const TeamMemberGroups = () => {
    const {
        data: {canEditTeam, groups = [], name, navigationTabs, teamMemberMid},
        setData,
    } = useBackend<TeamMemberGroupsBackendData>();

    const methods = useForm();

    const {buttonProps, isPending, mutateAsync} =
        useToggleTeamMemberGroup(teamMemberMid);

    const sortedGroups = useMemo(() => {
        const sortByMembership = (groupA: Group, groupB: Group) =>
            (groupB.members?.some((member) => member.mid === teamMemberMid)
                ? 1
                : 0) -
            (groupA.members?.some((member) => member.mid === teamMemberMid)
                ? 1
                : 0);

        return groups.reverse().sort(sortByMembership);
    }, [groups, teamMemberMid]);

    const {paginatedData, ...pagination} = usePaginatedData(sortedGroups, 20);

    const handleToggleGroup = (groupMid: string) => {
        mutateAsync(groupMid, {groupMid})
            .then(
                onApiSuccess({
                    onRedirect: (response) => {
                        if (response.name === 'upgrade_groups') {
                            return setUrl(window.Urls.upgrade_groups());
                        }
                        throw new Error('Unexpected response');
                    },
                    onSuccess: (response) => {
                        if (response.name === 'refreshData' && response.data) {
                            return setData(response.data);
                        }
                        throw new Error('Unexpected response');
                    },
                }),
            )
            .catch(onApiError(methods));
    };

    const teamCreateViewLink = (
        <Link
            href={window.Urls.team_create_view('group')}
            key="create-one-now"
        >{t`create one now`}</Link>
    );

    return (
        <FormProvider {...methods}>
            <FormResult />
            <SectionHeader title={name} />
            <NavigationTabs.TeamMember
                className="my-4"
                mid={teamMemberMid}
                selection="groups"
                {...navigationTabs}
            />
            <p className="mb-3 mt-6">{t`Documents and clients owned by a group are only visible to members of the group`}</p>
            <TableStatic className="mb-4 min-w-fit" id="groups-table">
                <TableStatic.Header>
                    <TableStatic.HeaderRow>
                        <TableStatic.Column className="whitespace-nowrap">
                            {t`Group Name`}
                        </TableStatic.Column>
                        <TableStatic.Column className="whitespace-nowrap text-center">
                            {t`Member`}
                        </TableStatic.Column>
                        <TableStatic.Column className="whitespace-nowrap text-center">
                            {t`Member Count`}
                        </TableStatic.Column>
                        <TableStatic.Column className="whitespace-nowrap text-center">
                            {t`Client Count`}
                        </TableStatic.Column>
                        <TableStatic.Column />
                    </TableStatic.HeaderRow>
                </TableStatic.Header>
                <TableStatic.Body>
                    {paginatedData.length > 0 ? (
                        paginatedData.map(
                            (group) =>
                                group && (
                                    <TableStatic.Row key={group.mid}>
                                        <TableStatic.Cell>
                                            <Link
                                                href={window.Urls.team_item_view(
                                                    group.mid,
                                                )}
                                            >
                                                {group.name}
                                            </Link>
                                        </TableStatic.Cell>
                                        <TableStatic.Cell>
                                            {group.members?.some(
                                                (member) =>
                                                    member.mid ===
                                                    teamMemberMid,
                                            ) && (
                                                <Icon
                                                    className="flex justify-center text-green-500"
                                                    icon="success"
                                                    size="base"
                                                />
                                            )}
                                        </TableStatic.Cell>
                                        <TableStatic.Cell className="whitespace-nowrap text-center">
                                            {group.members?.length > 0 &&
                                                group.members?.length}
                                        </TableStatic.Cell>
                                        <TableStatic.Cell className="whitespace-nowrap text-center">
                                            {group.clientCount > 0 &&
                                                group.clientCount}
                                        </TableStatic.Cell>
                                        <TableStatic.Cell className="flex justify-end">
                                            <TooltipWrapper
                                                message={
                                                    canEditTeam.failureReason
                                                }
                                            >
                                                <Button
                                                    disabled={
                                                        !canEditTeam.allowed ||
                                                        isPending
                                                    }
                                                    {...buttonProps[group.mid]}
                                                    kind="secondary"
                                                    onClick={() =>
                                                        handleToggleGroup(
                                                            group.mid,
                                                        )
                                                    }
                                                    size="xs"
                                                >
                                                    {group.members?.some(
                                                        (member) =>
                                                            member.mid ===
                                                            teamMemberMid,
                                                    )
                                                        ? t`Remove from group`
                                                        : t`Add to group`}
                                                </Button>
                                            </TooltipWrapper>
                                        </TableStatic.Cell>
                                    </TableStatic.Row>
                                ),
                        )
                    ) : (
                        <TableStatic.Row>
                            <TableStatic.Cell>
                                {jt`You haven't created any groups yet, ${teamCreateViewLink}.`}
                            </TableStatic.Cell>
                        </TableStatic.Row>
                    )}
                </TableStatic.Body>
            </TableStatic>
            <TablePagination
                hideForSinglePage={true}
                id="groups-pagination"
                {...pagination}
            />
        </FormProvider>
    );
};

export default TeamMemberGroups;
